import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, Search, Receipt, CreditCard, Plus, RotateCcw, Trash2 } from "lucide-react";

const STATUS_CLS = {
  paid: "bg-[#ECFBEF] text-[#0A7F2E]",
  simulated: "bg-[#FFF6E8] text-[#A65B00]",
  initiated: "bg-[#FFFBE6] text-[#9A7D00]",
  expired: "bg-surface text-subtle",
  unpaid: "bg-[#FDE2E2] text-destructive",
  canceled: "bg-[#FDE2E2] text-destructive",
};

// Spec utilisateur fork 26 — libellés FR explicites pour chaque statut.
const STATUS_LABEL = {
  paid: "Payé",
  simulated: "Paiement simulé",
  initiated: "Initié",
  canceled: "Paiement refusé",
  expired: "Expiré",
  unpaid: "Non payé",
};
const statusLabel = (s) => STATUS_LABEL[s] || s || "—";

// Translates the raw `purpose` enum into a French label + an icon for the
// totals cards. Keeps a graceful fallback for purposes we don't know yet
// (forwards-compat). Spec utilisateur fork 26 — `priority_ticket` retiré
// (le visiteur ne paie plus de tickets prioritaires).
const PURPOSE_LABEL = {
  merchant_subscription: { fr: "Abonnement étab.", icon: CreditCard },
  additional_queue: { fr: "File supplémentaire", icon: Plus },
  queue_first_ponctuel: { fr: "File ponctuelle", icon: Receipt },
  queue_renewal: { fr: "Renouvellement file", icon: RotateCcw },
};

const labelFor = (p) => PURPOSE_LABEL[p]?.fr || p || "—";

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [purpose, setPurpose] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        status: status || undefined,
        purpose: purpose || undefined,
        search: search.trim() || undefined,
        // datetime-local "YYYY-MM-DDTHH:mm" → backend expects ISO. We send
        // as-is (the backend uses lexicographic comparison on the ISO
        // string, which works for these inputs).
        date_from: dateFrom ? `${dateFrom}:00.000Z` : undefined,
        date_to: dateTo ? `${dateTo}:59.999Z` : undefined,
        limit: 500,
      };
      const { data } = await api.get("/admin/payments", { params });
      setData(data);
    } catch {
      setData({ total: 0, items: [], totals: {}, grand_paid_amount_eur: 0 });
    } finally {
      setLoading(false);
    }
  };

  const deleteOne = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm("Supprimer définitivement cette transaction de l'historique ? Action irréversible.")) return;
    try {
      await api.delete(`/admin/payments/${encodeURIComponent(sessionId)}`);
      toast.success("Transaction supprimée.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression.");
    }
  };

  // Reload on every filter change. Manual reload also exposed via the
  // "Actualiser" button + the CSV export which always uses the current
  // filter set.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, purpose, dateFrom, dateTo]);

  // Debounce the free-text search so we don't fire one request per
  // keystroke. 350ms is fast enough to feel instant on the admin side.
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const totalsArr = useMemo(() => {
    if (!data?.totals) return [];
    return Object.entries(data.totals).sort((a, b) => b[1].paid_amount_eur - a[1].paid_amount_eur);
  }, [data]);

  const exportXlsx = async () => {
    // Spec utilisateur fork 26 (Feb 2026) — l'export Excel n'utilise PAS
    // les filtres écran : il exporte la TOTALITÉ des paiements RÉELS
    // validés (`paid`, hors simulés / test / annulés / remboursés), classés
    // par mois/année dans des feuilles dédiées. Le backend gère le tri
    // (du plus récent au plus ancien) et le nommage de fichier.
    try {
      const res = await api.get(`/admin/payments.xlsx`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Récupère le nom suggéré par le backend (Content-Disposition),
      // fallback sur un nom local conforme à la spec (AAAA-MM).
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const now = new Date();
      const fallback = `Paiements_BypassGo_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}.xlsx`;
      a.download = match?.[1] || fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // toast handled by axios interceptor — keep this silent here.
    }
  };

  const resetFilters = () => {
    setStatus("");
    setPurpose("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  if (!data) return <div className="text-subtle">Chargement…</div>;

  return (
    <div data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl font-black text-ink flex items-center gap-2">
          <Receipt size={22} /> Paiements
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={resetFilters}
            className="h-9 px-3 inline-flex items-center gap-1.5 border border-border text-ink hover:border-ink text-xs font-bold uppercase tracking-wider"
            data-testid="payments-reset-filters-btn"
            disabled={loading}
          >
            <RotateCcw size={12} /> Réinitialiser
          </button>
          <button
            onClick={exportXlsx}
            disabled={loading}
            className="h-9 px-4 inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="payments-export-xlsx-btn"
            title="Exporte tous les paiements réels validés (une feuille par mois)"
          >
            <Download size={12} /> Export Excel
          </button>
          <button
            onClick={async () => {
              if (!data?.items?.length) return;
              if (!window.confirm("Supprimer définitivement TOUTES les lignes affichées de l'historique des paiements ? Action irréversible.")) return;
              try {
                const params = new URLSearchParams();
                if (status) params.set("status", status);
                if (purpose) params.set("purpose", purpose);
                const { data: res } = await api.delete(`/admin/payments?${params.toString()}`);
                toast.success(`${res.deleted || 0} transaction(s) supprimée(s).`);
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Erreur lors de la suppression.");
              }
            }}
            disabled={loading || !data?.items?.length}
            className="h-9 px-4 inline-flex items-center gap-1.5 bg-destructive hover:bg-destructive/90 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="payments-delete-all-btn"
          >
            <Trash2 size={12} /> Tout supprimer
          </button>
        </div>
      </div>

      {/* CARDS : totals par purpose (paid only) — donne une vue exécutive */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          label="Total encaissé"
          value={`${Number(data.grand_paid_amount_eur || 0).toFixed(2)} €`}
          sublabel={`${data.total || 0} transaction${(data.total || 0) > 1 ? "s" : ""}`}
          icon={Receipt}
          highlight
          testid="payments-card-total"
        />
        {totalsArr.map(([p, t]) => {
          const Icon = PURPOSE_LABEL[p]?.icon || Receipt;
          return (
            <SummaryCard
              key={p}
              label={labelFor(p)}
              value={`${Number(t.paid_amount_eur || 0).toFixed(2)} €`}
              sublabel={`${t.paid_count}/${t.count} payés`}
              icon={Icon}
              testid={`payments-card-${p}`}
            />
          );
        })}
      </div>

      {/* MONTHLY CHART : encaissements jour par jour du mois courant
         (basé sur les transactions paid dans la liste filtrée). SVG natif
         sans dépendance — léger et imprimable. */}
      <MonthlyChart items={data.items || []} />

      {/* FILTRES */}
      <div className="border border-border bg-surface/30 p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher (étab., visiteur, email, session…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full pl-8 pr-2 border border-border text-xs"
            data-testid="payments-search-input"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 border border-border px-2 text-xs" data-testid="payments-filter-status">
          <option value="">Tous statuts</option>
          <option value="paid">Payé</option>
          <option value="simulated">Paiement simulé</option>
          <option value="initiated">Initié</option>
          <option value="canceled">Paiement refusé</option>
          <option value="expired">Expiré</option>
          <option value="unpaid">Non payé</option>
        </select>
        <select value={purpose} onChange={(e) => setPurpose(e.target.value)} className="h-9 border border-border px-2 text-xs" data-testid="payments-filter-purpose">
          <option value="">Tous types</option>
          <option value="merchant_subscription">Abonnement étab.</option>
          <option value="queue_first_ponctuel">File ponctuelle</option>
          <option value="additional_queue">File supplémentaire</option>
          <option value="queue_renewal">Renouvellement file</option>
        </select>
        <div className="grid grid-cols-2 gap-1">
          <input type="datetime-local" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 border border-border px-2 text-[11px]" data-testid="payments-filter-from" title="Du" />
          <input type="datetime-local" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 border border-border px-2 text-[11px]" data-testid="payments-filter-to" title="Au" />
        </div>
      </div>

      {/* TABLE desktop */}
      <div className="border border-border overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-surface/60 text-subtle text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Montant</th>
              <th className="text-left p-3">Compte</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Session</th>
              <th className="text-right p-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-subtle">Aucun paiement</td></tr>}
            {data.items.map((t) => (
              <tr key={t.session_id} className="border-t border-border" data-testid={`payment-row-${t.session_id}`}>
                <td className="p-3 text-subtle text-xs whitespace-nowrap">{new Date(t.createdAt).toLocaleString("fr-FR")}</td>
                <td className="p-3 text-xs">{labelFor(t.purpose)}</td>
                <td className="p-3 font-mono font-bold whitespace-nowrap">{Number(t.amount || 0).toFixed(2)} €</td>
                <td className="p-3 text-xs">
                  {t.userName ? (
                    <>
                      {t.userName} {t.userDeleted && <span className="ml-1 text-[9px] uppercase tracking-wider text-destructive font-bold">supprimé</span>}
                      <br /><span className="text-subtle">{t.userEmail}</span>
                    </>
                  ) : null}
                  {t.commerceName ? (
                    <>
                      {t.commerceName} {t.commerceDeleted && <span className="ml-1 text-[9px] uppercase tracking-wider text-destructive font-bold">supprimé</span>}
                      <br /><span className="text-subtle">{t.commerceEmail}</span>
                    </>
                  ) : null}
                </td>
                <td className="p-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[t.payment_status] || "bg-surface text-subtle"}`}>
                    {statusLabel(t.payment_status)}
                  </span>
                </td>
                <td className="p-3 font-mono text-[10px] text-subtle truncate max-w-[140px]">{t.session_id}</td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => deleteOne(t.session_id)}
                    className="inline-flex items-center justify-center w-7 h-7 text-destructive hover:bg-destructive/10 rounded"
                    title="Supprimer cette transaction"
                    data-testid={`payment-delete-row-${t.session_id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE cards — fork 19 responsive admin */}
      <div className="md:hidden space-y-2" data-testid="payments-cards-mobile">
        {data.items.length === 0 && <div className="border border-border p-6 text-center text-subtle text-sm">Aucun paiement</div>}
        {data.items.map((t) => (
          <div key={t.session_id} className="border border-border bg-white p-3" data-testid={`payment-card-${t.session_id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="font-mono font-black text-lg text-ink">{Number(t.amount || 0).toFixed(2)} €</p>
              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${STATUS_CLS[t.payment_status] || "bg-surface text-subtle"}`}>{statusLabel(t.payment_status)}</span>
            </div>
            <p className="text-xs text-ink">{labelFor(t.purpose)}</p>
            <p className="text-[11px] text-subtle">{new Date(t.createdAt).toLocaleString("fr-FR")}</p>
            {(t.userName || t.commerceName) && (
              <div className="mt-1 text-[11px] text-subtle">
                <p>
                  <strong className="text-ink">{t.userName || t.commerceName}</strong>
                  {(t.userDeleted || t.commerceDeleted) && <span className="ml-1 text-[9px] uppercase tracking-wider text-destructive font-bold">supprimé</span>}
                </p>
                <p className="font-mono truncate">{t.userEmail || t.commerceEmail}</p>
              </div>
            )}
            <p className="text-[10px] font-mono text-subtle mt-1 truncate">Session : {t.session_id}</p>
            <div className="mt-2 pt-2 border-t border-border flex justify-end">
              <button
                onClick={() => deleteOne(t.session_id)}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-destructive uppercase tracking-wider"
                data-testid={`payment-delete-card-${t.session_id}`}
              >
                <Trash2 size={11} /> Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sublabel, icon: Icon, highlight = false, testid }) {
  return (
    <div
      className={`p-3 border ${highlight ? "border-ink bg-ink/5" : "border-border bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className={highlight ? "text-ink" : "text-subtle"} />}
        <p className="text-[10px] uppercase tracking-wider font-bold text-subtle truncate">{label}</p>
      </div>
      <p className={`font-mono font-black text-xl truncate ${highlight ? "text-ink" : "text-ink"}`}>{value}</p>
      {sublabel && <p className="text-[10px] text-subtle mt-0.5">{sublabel}</p>}
    </div>
  );
}

/**
 * Monthly bar chart of daily PAID revenue for the current month.
 * Pure SVG (no chart library) — light, printable, mobile-friendly.
 * Reads from the current `items` list so it honours all active filters
 * (status, purpose, search, date range) — gives an instant visual of
 * what the admin is looking at.
 */
function MonthlyChart({ items }) {
  // Build a date → total map for paid transactions over the current month.
  // We bucket by local YYYY-MM-DD so timezone alignment matches the rest
  // of the admin UI (toLocaleDateString uses local TZ everywhere else).
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const buckets = new Array(daysInMonth).fill(0);
  for (const t of items) {
    if (t.payment_status !== "paid") continue;
    const d = new Date(t.createdAt);
    if (d.getFullYear() !== year || d.getMonth() !== month) continue;
    buckets[d.getDate() - 1] += Number(t.amount || 0);
  }
  const max = Math.max(1, ...buckets); // avoid /0
  const total = buckets.reduce((a, b) => a + b, 0);
  const monthLabel = today.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div className="border border-border bg-white p-4 mb-5" data-testid="payments-monthly-chart">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="overline font-bold text-ink">📊 Encaissements quotidiens — {monthLabel}</p>
        <p className="text-xs text-subtle">
          Total mois : <strong className="text-ink font-mono">{total.toFixed(2)} €</strong>
        </p>
      </div>
      <svg
        viewBox={`0 0 ${daysInMonth * 16} 100`}
        className="w-full h-24"
        preserveAspectRatio="none"
        role="img"
        aria-label="Graphique des encaissements quotidiens du mois en cours"
      >
        {/* Baseline */}
        <line x1="0" y1="90" x2={daysInMonth * 16} y2="90" stroke="#e5e7eb" strokeWidth="0.5" />
        {buckets.map((v, i) => {
          const h = (v / max) * 80;
          const x = i * 16 + 2;
          const y = 90 - h;
          const isToday = i + 1 === today.getDate();
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={12}
                height={Math.max(0.5, h)}
                fill={isToday ? "#002FA7" : v > 0 ? "#0A7F2E" : "#e5e7eb"}
                data-testid={`chart-bar-${i + 1}`}
              >
                <title>{`${i + 1}/${month + 1} : ${v.toFixed(2)} €`}</title>
              </rect>
              {/* Day label every 5 days for legibility */}
              {(i % 5 === 0 || i === daysInMonth - 1) && (
                <text x={x + 6} y="99" fontSize="6" textAnchor="middle" fill="#6b7280" fontFamily="ui-monospace, monospace">
                  {i + 1}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-subtle">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-[#0A7F2E]" /> Jour payé</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-[#002FA7]" /> Aujourd'hui</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-zinc-200" /> Aucun paiement</span>
      </div>
    </div>
  );
}
